/**
 * Global Property Tree View Component
 * 
 * Hierarchical tree view of property relationships showing dependencies
 * and option catalog connections.
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Card,
  Tree,
  Input,
  Space,
  Button,
  Typography,
  Tag,
  Spin,
  message,
  Empty
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  DatabaseOutlined,
  LinkOutlined
} from '@ant-design/icons';
import { GlobalPropertiesService } from '../../services/api/globalPropertiesService';
import {
  PropertyDefinition,
  PropertyDefinitionResponse,
  PropertyDependency
} from '../../types/globalProperties';
import { dependencyKeysFromCalculationDependencies } from '../../utils/calculationDependencyKeys';

const { Search } = Input;
const { Title, Text } = Typography;

interface GlobalPropertyTreeViewProps {
  propertyKeys?: string[]; // Optional: filter to specific properties
  /** When provided, skip N+1 getPropertyDefinition calls. */
  propertyDefinitions?: PropertyDefinition[];
  onPropertySelect?: (propertyKey: string) => void;
  rootPropertyKey?: string; // Optional: show tree starting from a specific property
}

interface TreeNode {
  title: React.ReactNode;
  key: string;
  propertyKey: string;
  propertyType: string;
  isLeaf?: boolean;
  children?: TreeNode[];
  nodeType: 'property' | 'option_catalog';
  catalogId?: string;
}

export const GlobalPropertyTreeView: React.FC<GlobalPropertyTreeViewProps> = ({
  propertyKeys,
  propertyDefinitions,
  onPropertySelect,
  rootPropertyKey
}) => {
  const [properties, setProperties] = useState<Map<string, PropertyDefinition>>(new Map());
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

  const loadProperties = useCallback(async () => {
    setLoading(true);
    try {
      const propertiesMap = new Map<string, PropertyDefinition>();

      if (propertyDefinitions && propertyDefinitions.length > 0) {
        for (const def of propertyDefinitions) {
          const key = def.property_key;
          if (key) propertiesMap.set(key, def);
        }
      } else if (propertyKeys && propertyKeys.length > 0) {
        for (const key of propertyKeys) {
          try {
            const response = await GlobalPropertiesService.getPropertyDefinition(key);
            propertiesMap.set(key, response.definition);
          } catch (error) {
            console.warn(`Failed to load property ${key}:`, error);
          }
        }
      } else if (rootPropertyKey) {
        // Load root property and recursively load dependencies
        await loadPropertyRecursive(rootPropertyKey, propertiesMap);
      }
      
      setProperties(propertiesMap);
      
      // Auto-expand root if specified
      if (rootPropertyKey) {
        setExpandedKeys([rootPropertyKey]);
      }
    } catch (error: any) {
      console.error('Error loading properties:', error);
      message.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  }, [propertyKeys, propertyDefinitions, rootPropertyKey]);

  const loadPropertyRecursive = async (
    propertyKey: string,
    propertiesMap: Map<string, PropertyDefinition>,
    visited: Set<string> = new Set()
  ) => {
    if (visited.has(propertyKey)) return; // Prevent circular dependencies
    visited.add(propertyKey);

    try {
      const response = await GlobalPropertiesService.getPropertyDefinition(propertyKey);
      propertiesMap.set(propertyKey, response.definition);

      // Load dependencies (inputs[], nested configs, legacy source_properties)
      const depKeys = dependencyKeysFromCalculationDependencies(
        response.definition.calculation_dependencies as Record<string, unknown> | undefined
      );
      for (const depKey of depKeys) {
        if (!visited.has(depKey)) {
          await loadPropertyRecursive(depKey, propertiesMap, visited);
        }
      }
    } catch (error) {
      console.warn(`Failed to load property ${propertyKey}:`, error);
    }
  };

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  const buildTreeData = useCallback((): TreeNode[] => {
    if (rootPropertyKey && properties.has(rootPropertyKey)) {
      // Build tree starting from root
      return [buildTreeNode(rootPropertyKey, new Set())];
    }

    // Build forest from all properties (no dependencies)
    const rootProperties: TreeNode[] = [];
    const processed = new Set<string>();

    properties.forEach((def, key) => {
      // Only show as root if it's not a dependency of another property
      const isDependency = Array.from(properties.values()).some((prop) =>
        dependencyKeysFromCalculationDependencies(
          prop.calculation_dependencies as Record<string, unknown> | undefined
        ).includes(key)
      );

      if (!isDependency && !processed.has(key)) {
        rootProperties.push(buildTreeNode(key, processed));
      }
    });

    return rootProperties;
  }, [properties, rootPropertyKey]);

  const buildTreeNode = (propertyKey: string, processed: Set<string>, path: string = ''): TreeNode => {
    // Create unique key by including the path to this node
    const uniqueKey = path ? `${path}::${propertyKey}` : propertyKey;
    
    if (processed.has(propertyKey)) {
      return {
        title: <Text>{propertyKey} (circular)</Text>,
        key: uniqueKey,
        propertyKey,
        propertyType: 'string',
        nodeType: 'property',
        isLeaf: true
      };
    }

    processed.add(propertyKey);
    const def = properties.get(propertyKey);
    if (!def) {
      return {
        title: <Text type="danger">{propertyKey} (not found)</Text>,
        key: uniqueKey,
        propertyKey,
        propertyType: 'string',
        nodeType: 'property',
        isLeaf: true
      };
    }

    const children: TreeNode[] = [];

    // Add option catalog node if linked
    if (def.global_option_catalog_ref) {
      children.push({
        title: (
          <Space>
            <DatabaseOutlined />
            <Text strong>Option Catalog</Text>
            <Tag color="cyan">{def.global_option_catalog_ref.substring(0, 8)}...</Tag>
          </Space>
        ),
        key: `${uniqueKey}::catalog_${def.global_option_catalog_ref}`,
        propertyKey: def.global_option_catalog_ref,
        propertyType: 'option_catalog',
        nodeType: 'option_catalog',
        catalogId: def.global_option_catalog_ref,
        isLeaf: true
      });
    }

    // Add dependency nodes (inputs[], nested configs, legacy source_properties)
    const depKeys = dependencyKeysFromCalculationDependencies(
      def.calculation_dependencies as Record<string, unknown> | undefined
    );
    for (const childKey of depKeys) {
      if (properties.has(childKey)) {
        children.push(buildTreeNode(childKey, new Set(processed), uniqueKey));
      } else {
        children.push({
          title: (
            <Space>
              <LinkOutlined />
              <Text>{childKey}</Text>
              <Tag color="orange">Not loaded</Tag>
            </Space>
          ),
          key: `${uniqueKey}::${childKey}`,
          propertyKey: childKey,
          propertyType: 'string',
          nodeType: 'property',
          isLeaf: true
        });
      }
    }

    return {
      title: (
        <Space>
          <Text strong>{propertyKey}</Text>
          <Tag color="blue">{def.property_type}</Tag>
          {def.description && <Text type="secondary" style={{ fontSize: '12px' }}>({def.description})</Text>}
        </Space>
      ),
      key: uniqueKey,
      propertyKey,
      propertyType: def.property_type,
      nodeType: 'property',
      children: children.length > 0 ? children : undefined,
      isLeaf: children.length === 0
    };
  };

  const treeData = useMemo(() => {
    let data = buildTreeData();

    // Filter by search text
    if (searchText) {
      const filterTree = (nodes: TreeNode[]): TreeNode[] => {
        const filtered: TreeNode[] = [];
        for (const node of nodes) {
          const matches = node.propertyKey.toLowerCase().includes(searchText.toLowerCase());
          const filteredChildren = node.children ? filterTree(node.children) : undefined;
          const hasMatchingChildren = filteredChildren && filteredChildren.length > 0;

          if (matches || hasMatchingChildren) {
            filtered.push({
              ...node,
              children: filteredChildren
            });
          }
        }
        return filtered;
      };

      data = filterTree(data);
    }

    return data;
  }, [buildTreeData, searchText]);

  const handleSelect = (selectedKeys: React.Key[]) => {
    setSelectedKeys(selectedKeys);
    if (selectedKeys.length > 0) {
      const key = selectedKeys[0];
      // Ensure key is a string before calling startsWith
      const keyStr = typeof key === 'string' ? key : String(key);
      if (!keyStr.startsWith('catalog_')) {
        onPropertySelect?.(keyStr);
      }
    }
  };

  const handleExpand = (expandedKeys: React.Key[]) => {
    setExpandedKeys(expandedKeys);
  };

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>Property Relationships Tree</Title>
          {rootPropertyKey && <Tag>{rootPropertyKey}</Tag>}
        </Space>
      }
      extra={
        <Space>
          <Search
            placeholder="Search properties..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={loadProperties}>Refresh</Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        {treeData.length > 0 ? (
          <Tree
            treeData={treeData}
            expandedKeys={expandedKeys}
            selectedKeys={selectedKeys}
            onSelect={handleSelect}
            onExpand={handleExpand}
            defaultExpandAll={!!rootPropertyKey}
            showLine={{ showLeafIcon: false }}
            style={{ minHeight: '400px' }}
          />
        ) : (
          <Empty
            description={
              <Text type="secondary">
                {searchText ? 'No properties match your search' : 'No properties loaded. Load properties to see the tree.'}
              </Text>
            }
          />
        )}
      </Spin>
    </Card>
  );
};

